/**
 * Production-line batch bonuses, read from the daily production report.
 *
 * Michael, 2026-10-28, replacing the Excel button ("It's just not working"):
 *
 *  - at 8:00 every morning WorkLogix reads YESTERDAY's tab of the production report
 *    workbook on SharePoint and records how many batches each line completed;
 *  - every 13 batches a line completes in one day earns that line $20 for the day
 *    (26 batches = $40);
 *  - at 8:00 on Wednesday, after the daily read, each line's bonuses for the payroll
 *    week that just closed are summed and posted to every employee a supervisor has put
 *    on that line's crew, as an entry in the Bonus report.
 *
 * 🔥 THE CREW IS A LIST OF EMPLOYEE IDS, CHOSEN BY A PERSON. The spreadsheet import
 * matched names typed on a sheet, and only 6 of 29 matched exactly — the near-misses were
 * other men on the same sheet. Nothing here reads a name: a supervisor picks the employee
 * from the roster once, and that choice is what gets paid.
 *
 * Pure and dependency-free, so the API, the job and the browser agree on the rules.
 */
/** The lines on the report, in the order the workbook lays them out. */
export const PRODUCTION_LINES = [
    { code: 'line-1', label: 'Line 1', sheetLabel: '1' },
    { code: 'line-2', label: 'Line 2', sheetLabel: '2' },
    { code: 'seed', label: 'Seed Line', sheetLabel: 'Seed' },
    { code: 'fertilizer', label: 'Fertilizer', sheetLabel: 'Fertilizer' },
];
/** Batches a line must complete in one day for each $20. */
export const BATCHES_PER_LINE_BONUS = 13;
/** Dollars a line earns for each full {@link BATCHES_PER_LINE_BONUS} in a day. */
export const LINE_BONUS_USD = 20;
/** `amount_entry.source_system` for these bonuses (see migration 0066). */
export const PRODUCTION_LINE_BONUS_SOURCE_SYSTEM = 'production-line-bonus';
export function isProductionLineCode(value) {
    return PRODUCTION_LINES.some((l) => l.code === value);
}
export function productionLineLabel(code) {
    return PRODUCTION_LINES.find((l) => l.code === code)?.label ?? code;
}
/**
 * One day's bonus for a line. FLOORED — 25 batches is one block, not "nearly two".
 * A negative or non-finite count (a bad read) earns nothing rather than a refund.
 */
export function productionLineDayBonus(batches) {
    if (!Number.isFinite(batches) || batches <= 0) {
        return 0;
    }
    return Math.floor(batches / BATCHES_PER_LINE_BONUS) * LINE_BONUS_USD;
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/**
 * The tab name the workbook uses for a date: `Tue 10-27-26`. No zero padding.
 * @param date ISO `YYYY-MM-DD`.
 */
export function productionSheetName(date) {
    const y = Number(date.slice(0, 4));
    const m = Number(date.slice(5, 7));
    const d = Number(date.slice(8, 10));
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return `${WEEKDAYS[dow] ?? ''} ${String(m)}-${String(d)}-${String(y % 100).padStart(2, '0')}`;
}
/**
 * The date a tab is for, or null when the name is not a day tab ("October Monthly
 * Summary", "Lists"). Tolerant of zero padding, a four-digit year and extra spaces —
 * whoever adds next month's tabs types them by hand. The weekday word is ignored: the
 * date decides, and a mistyped weekday must not hide a day's batches.
 */
export function parseProductionSheetName(name) {
    const match = /^\s*[A-Za-z]{3,9}\.?\s+(\d{1,2})-(\d{1,2})-(\d{2}|\d{4})\s*$/.exec(name);
    if (match === null) {
        return null;
    }
    const month = Number(match[1]);
    const day = Number(match[2]);
    const rawYear = Number(match[3]);
    const year = rawYear < 100 ? 2000 + rawYear : rawYear;
    const ms = Date.UTC(year, month - 1, day);
    const back = new Date(ms);
    if (back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) {
        return null;
    }
    return back.toISOString().slice(0, 10);
}
/**
 * The month a workbook file covers, from its name: `10-2026 Production Report - Feed
 * Seed.xlsx` → `{ year: 2026, month: 10 }`. Null for anything else in the folder.
 */
export function productionReportFileMonth(fileName) {
    const match = /^(\d{1,2})-(\d{4}) Production Report\b.*\.xlsx$/i.exec(fileName.trim());
    if (match === null) {
        return null;
    }
    const month = Number(match[1]);
    if (month < 1 || month > 12) {
        return null;
    }
    return { year: Number(match[2]), month };
}
/**
 * Which monthly files may hold a date's tab, most likely first.
 *
 * 🔥 NOT JUST THE DATE'S OWN MONTH. A file runs in whole Monday-to-Sunday weeks: the
 * October 2026 file starts Mon 10-5 and ends **Sun 11-1**. So the read on the morning of
 * Mon 11-2 wants a tab that lives in the OCTOBER file, and looking only in November's
 * would find nothing and record a silent zero for every line.
 */
export function candidateFileMonths(date) {
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const prev = month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
    return [{ year, month }, prev];
}
/** How a line label in column A ("LINE: Seed") maps to a line, or null if unknown. */
export function lineCodeForSheetLabel(label) {
    const wanted = label
        .replace(/^\s*line\s*:?/i, '')
        .replace(/\s+line\s*$/i, '')
        .trim()
        .toLowerCase();
    const hit = PRODUCTION_LINES.find((l) => l.sheetLabel.toLowerCase() === wanted);
    return hit?.code ?? null;
}
/** Column positions in a day tab (0-based): A = slot, B = work order, C = product, H = End. */
const COL_SLOT = 0;
const COL_WORK_ORDER = 1;
const COL_PRODUCT = 2;
const COL_END = 7;
function isBlank(value) {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string') {
        return value.trim() === '';
    }
    return false;
}
/**
 * Count the completed batches per line on one day tab.
 *
 * Takes the tab as a GRID of plain cell values (row-major, column A first) so the rule
 * is testable without a workbook library; the API flattens exceljs into this.
 *
 * Layout (verified 2026-10-28 against the October 2026 file): each line is a block
 * headed `LINE: <name>` in column A, then a header row, then numbered slots 1–32.
 *
 * 🔥 A BATCH IS A FILLED ROW THAT AN END TIME CLOSES — NOT "A ROW WITH AN END TIME".
 * Several work orders run back to back are timed as ONE run: Start on the first row,
 * End on the last, the rows between blank (Seed, Thu 10-22-26, slots 7–9: three HELENA
 * TCHULA totes, Start 1:08 PM on 7, End 3:25 PM on 9). The sheet's own TPH formula
 * reads it the same way, summing tons back to the last Start. Counting only rows with
 * an End would pay that run as one batch instead of three. So a row with a work order
 * or product counts once a later row in the same block (or itself) has an End; rows
 * after the block's last End are `unfinished`. Night shift is in
 * the same block and runs past midnight on the same tab, so day and night both count
 * toward the line's day — which is the rule Michael gave (21 batches on Line 1 on
 * Tue 10-27, 13 by days and 8 by nights).
 *
 * Every known line is returned, with zero when its block is empty or absent, so a quiet
 * line is recorded as a quiet line rather than as a missing read.
 */
export function countProductionBatches(grid) {
    const totals = new Map();
    const unknownLines = [];
    // null = in an unknown block (rows ignored), undefined = before any block.
    let current;
    // Filled rows since the last End in this block — they belong to the run that the
    // next End closes.
    let open = 0;
    const closeBlock = () => {
        const tally = current === null || current === undefined ? undefined : totals.get(current);
        if (tally !== undefined) {
            tally.unfinished += open;
        }
        open = 0;
    };
    for (const row of grid) {
        const first = row[COL_SLOT];
        if (typeof first === 'string' && /^\s*line\s*:/i.test(first)) {
            closeBlock();
            const label = first.replace(/^\s*line\s*:/i, '').trim();
            current = lineCodeForSheetLabel(label);
            if (current === null) {
                unknownLines.push(label);
            }
            else if (!totals.has(current)) {
                totals.set(current, { batches: 0, unfinished: 0 });
            }
            continue;
        }
        if (current === null || current === undefined) {
            continue;
        }
        // Batch rows are the numbered slots. The header row ("Work Order") and blank
        // separator rows have no number in column A.
        const slot = typeof first === 'number' ? first : Number(String(first ?? '').trim());
        if (isBlank(first) || !Number.isInteger(slot) || slot <= 0) {
            continue;
        }
        const tally = totals.get(current);
        if (tally === undefined) {
            continue;
        }
        const filled = !isBlank(row[COL_WORK_ORDER]) || !isBlank(row[COL_PRODUCT]);
        if (!isBlank(row[COL_END])) {
            // This End closes every filled row since the previous one, and this row itself
            // (an End on an otherwise empty row still marks a finished batch).
            tally.batches += open + 1;
            open = 0;
        }
        else if (filled) {
            open += 1;
        }
    }
    closeBlock();
    return {
        counts: PRODUCTION_LINES.map((l) => ({
            line: l.code,
            batches: totals.get(l.code)?.batches ?? 0,
            unfinished: totals.get(l.code)?.unfinished ?? 0,
        })),
        unknownLines,
    };
}
/**
 * The idempotency key for one crew member's weekly line bonus. Line + week + employee:
 * a re-run of the Wednesday post finds it and creates nothing, while a person on two
 * crews still gets both (they accumulate, as they did on the spreadsheet).
 */
export function productionLineBonusRef(line, payPeriodEnding, employeeId) {
    return `${line}:${payPeriodEnding}:${employeeId}`;
}
/** The note on the bonus entry, so a supervisor can see where the figure came from. */
export function productionLineBonusNote(line, payPeriodEnding, batches, bonusDays) {
    return (`${productionLineLabel(line)} batch bonus, week ending ${payPeriodEnding}: ` +
        `${String(batches)} batches, ${String(bonusDays)} bonus day${bonusDays === 1 ? '' : 's'}`);
}
//# sourceMappingURL=production-line-bonus.js.map